import { Copy, Check, Navigation, MapPin } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

// Sitewide standard for any map/address link: Google Maps · Apple Maps · Copy address.
const MAPS_RE = /^(https?:)?\/\/(www\.)?(maps\.google\.com|google\.com\/maps|maps\.apple\.com|goo\.gl\/maps|maps\.app\.goo\.gl)/i;
export const isMapsUrl = (u) => MAPS_RE.test(u || "");

export const copyToClipboard = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* fall through */ }
  try {
    const ta = document.createElement("textarea"); ta.value = text; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select(); const ok = document.execCommand("copy"); document.body.removeChild(ta); return ok;
  } catch { return false; }
};

export const mapsQueryFromUrl = (u) => {
  try {
    const url = new URL(u, "https://x.invalid");
    const q = url.searchParams.get("q") || url.searchParams.get("query") || url.searchParams.get("destination") || url.searchParams.get("daddr") || url.searchParams.get("address");
    if (q) return q.replace(/\+/g, " ");
    const m = url.pathname.match(/\/maps\/(?:search|dir|place)\/([^/]+)/);
    return m ? decodeURIComponent(m[1]).replace(/\+/g, " ") : null;
  } catch { return null; }
};

const q = (address, lat, lng) => address || (lat != null && lng != null ? `${lat},${lng}` : "");
export const googleMapsUrl = (address, lat, lng, mode = "directions") =>
  mode === "directions" ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q(address, lat, lng))}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q(address, lat, lng))}`;
export const appleMapsUrl = (address, lat, lng, mode = "directions") => {
  const base = "https://maps.apple.com/?";
  if (address) return base + (mode === "directions" ? `daddr=${encodeURIComponent(address)}` : `q=${encodeURIComponent(address)}`);
  return base + (mode === "directions" ? `daddr=${lat},${lng}` : `ll=${lat},${lng}&q=${lat},${lng}`);
};

const SIZES = {
  xs: "text-[11px] px-2 py-1 gap-1 [&_svg]:h-3 [&_svg]:w-3",
  sm: "text-xs px-3 py-1.5 gap-1.5 [&_svg]:h-3.5 [&_svg]:w-3.5",
  md: "text-sm px-4 py-2 gap-2 [&_svg]:h-4 [&_svg]:w-4",
};

export const DirectionsActions = ({ address, lat, lng, mode = "directions", size = "sm", variant = "pills", accent = "#2E5C55", light = false, label, className = "", testId = "directions" }) => {
  const [copied, setCopied] = useState(false);
  const text = address || (lat != null && lng != null ? `${lat}, ${lng}` : "");
  if (!text) return null;
  const copy = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (await copyToClipboard(text)) { setCopied(true); toast.success("Address copied"); setTimeout(() => setCopied(false), 1800); }
    else toast.error("Couldn't copy — long-press the address instead");
  };
  const stop = (e) => e.stopPropagation();
  if (variant === "compact") {
    const c = `inline-flex items-center gap-1 font-semibold hover:underline no-underline ${size === "xs" ? "text-[11px]" : "text-xs"}`;
    const col = light ? "#fff" : accent;
    return (
      <span className={`inline-flex flex-wrap items-center gap-x-2 gap-y-1 ${className}`} data-testid={testId}>
        {label && <span className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</span>}
        <a href={googleMapsUrl(address, lat, lng, mode)} target="_blank" rel="noopener noreferrer" onClick={stop} className={c} style={{ color: col }} data-testid={`${testId}-google`}><Navigation className="h-3 w-3" /> Google</a>
        <span className="opacity-40" style={{ color: col }}>·</span>
        <a href={appleMapsUrl(address, lat, lng, mode)} target="_blank" rel="noopener noreferrer" onClick={stop} className={c} style={{ color: col }} data-testid={`${testId}-apple`}><MapPin className="h-3 w-3" /> Apple</a>
        <span className="opacity-40" style={{ color: col }}>·</span>
        <button type="button" onClick={copy} className={c} style={{ color: col }} data-testid={`${testId}-copy`}>{copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} {copied ? "Copied" : "Copy"}</button>
      </span>
    );
  }
  const base = `inline-flex items-center rounded-full font-bold transition-colors no-underline whitespace-nowrap ${SIZES[size] || SIZES.sm}`;
  const solid = light ? { backgroundColor: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)" } : { backgroundColor: accent, color: "#fff" };
  const outline = light ? { backgroundColor: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.35)" } : { backgroundColor: "#fff", color: accent, border: `1.5px solid ${accent}55` };
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid={testId}>
      {label && <span className="text-[10px] uppercase tracking-wider font-bold opacity-70 mr-1 w-full sm:w-auto">{label}</span>}
      <a href={googleMapsUrl(address, lat, lng, mode)} target="_blank" rel="noopener noreferrer" onClick={stop} className={base} style={solid} data-testid={`${testId}-google`}><Navigation /> Google Maps</a>
      <a href={appleMapsUrl(address, lat, lng, mode)} target="_blank" rel="noopener noreferrer" onClick={stop} className={base} style={outline} data-testid={`${testId}-apple`}><MapPin /> Apple Maps</a>
      <button type="button" onClick={copy} className={base} style={outline} data-testid={`${testId}-copy`}>{copied ? <Check /> : <Copy />} {copied ? "Copied" : "Copy address"}</button>
    </div>
  );
};

export default DirectionsActions;
